import { redirectToB2C } from '../pages/redirectTool';
import { ROUTE_LOGIN } from '../routing/paths';

export type AgentIdentity = {
    agentCode: string;
    accessB2C: boolean;
    accessB2B: boolean;
    nom?: string;
    nom_agent?: string;
    prenom_agent?: string;
    email?: string;
    tel?: string;
    companyName?: string;
    companyAddress?: string;
    companyLogo?: string;
    e_attestation?: boolean;
    code_fournisseur?: string;
    campagne?: string;
    siret?: string;
};

export interface ErrorBody<T> {
    statusCode: number;
    error: string;
    errorId: string;
    message: string;
    details?: T;
}

export const isErrorBody = <T = unknown>(value: unknown): value is ErrorBody<T> => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<ErrorBody<T>>;
    return typeof candidate.error === 'string' && typeof candidate.message === 'string' && typeof candidate.errorId === 'string';
};

type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestOptions = {
    method?: HttpMethod;
    body?: unknown;
    signal?: AbortSignal;
    redirectOn401?: boolean;
};

const buildApiUrl = (path: string): string => {
    const trimmedPath = path.replace(/^\/+/, '');
    return `${process.env.REACT_APP_URL_API}/${trimmedPath}`;
};

const handleUnauthorized = (response: Response, redirectOn401: boolean) => {
    if (response.status === 401 && redirectOn401) {
        console.warn('401 detected – redirecting to login');
        window.location.href = ROUTE_LOGIN;
    }
};

const parseJsonSafely = async (response: Response) => {
    const text = await response.text();
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch (error) {
        console.error('Failed to parse JSON response', error);
        return undefined;
    }
};

export const getData = async (url: string) => {
    const response = await fetch(`${process.env.REACT_APP_URL_API}/${url}`, {
        credentials: 'include',
        method: 'GET',
    });

    if (!response.ok) {
        // Si le code de statut n'est pas dans la plage [200, 299]
        if (response.status === 401) {
            // Le statut est 401 (Unauthorized)
            // Vous pouvez ici gérer la déconnexion de l'utilisateur
            // Par exemple, rediriger vers la page de login ou afficher un message d'erreur
            console.log('401 => redirection volontaire vers /login');
            redirectToB2C();
        }
        throw await response.json();
    }
    // // Don't know about side effect on this.
    // if (response.status === 201 || response.status === 204) {
    //     console.log('response empty, no JSON ');
    //     // may be rebuild a reply with a status since our
    //     return undefined;
    // }
    return await response.json();
};

export const requestJson = async <T = unknown>(path: string, options: RequestOptions = {}): Promise<T> => {
    const { method = 'POST', body, signal, redirectOn401 = true } = options;

    const response = await fetch(buildApiUrl(path), {
        method,
        credentials: 'include',
        headers: body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
        body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
        signal,
    });

    handleUnauthorized(response, redirectOn401);

    if (response.status === 204) {
        return undefined as unknown as T;
    }

    const data = await parseJsonSafely(response);

    if (!response.ok) {
        if (data) throw data;
        throw new Error(`Request failed with status ${response.status}`);
    }

    return data as T;
};

export const postJsonData = async <T = unknown>(path: string, body?: unknown, options: Omit<RequestOptions, 'body' | 'method'> = {}) => {
    return requestJson<T>(path, { ...options, body, method: 'POST' });
};

export const identify = async (agentCode: string, password: string): Promise<AgentIdentity> => {
    return postJsonData<AgentIdentity>(`identify?agentCode=${encodeURIComponent(agentCode)}&password=${encodeURIComponent(password)}`, undefined, {
        redirectOn401: false,
    });
};

//#region config files

// Récupère un certains nombre de fichiers de configuration et les stockes dans le local storage du navigateur.
// Ces fichiers sont lu du serveur directement par appels des routes associées.
export const getAllConfigFiles = async () => {
    let response;

    response = await getData('simulator/energyprices');
    localStorage.setItem('energyprices', JSON.stringify(response));

    response = await getData('simulator/photovoltaiqueconst');
    localStorage.setItem('constantesPhotovoltaiques', JSON.stringify(response));

    response = await getData('simulator/sunshines');
    localStorage.setItem('sunshines', JSON.stringify(response));

    response = await getData('simulator/sunshinesyield');
    localStorage.setItem('sunshinesyield', JSON.stringify(response));

    response = await getData('simulator/indexations');
    localStorage.setItem('indexations', JSON.stringify(response));

    response = await getData('simulator/recommendationsChauffage');
    localStorage.setItem('preconisationsChauffage', JSON.stringify(response));

    response = (await getData('investment-bonus/today')).elements;
    localStorage.setItem('investmentBonus', JSON.stringify(response));

    response = await getData(`simulator/constant/customerFlowParameters`);
    localStorage.setItem('customerFlowParameters', JSON.stringify(response));

    response = await getData(`investment-bonus/today`);
    localStorage.setItem('investmentBonus', JSON.stringify(response));

    response = await getData(`simulator/constant/locationTiersInvestisseur`);
    localStorage.setItem('kourbe', JSON.stringify(response));

    response = await getData(`simulator/constant/etudeDeChargePrice`);
    localStorage.setItem('etudeDeChargePrices', JSON.stringify(response));

    // Financo
    try {
        response = await getData('funding/financo/scales');
        localStorage.setItem('financoScales', JSON.stringify(response));
    } catch (ex) {
        // nothing to do. we are fucked !
        if (localStorage.getItem('financoScales') === null) {
            localStorage.setItem('financoScales', JSON.stringify([]));
        }
        // Sinon, on garde l'ancienne valeur.
    }
};

export const areAllConfigFilesReady = (): boolean => {
    if (localStorage.getItem('energyprices') === null) return false;
    if (localStorage.getItem('constantesPhotovoltaiques') === null) return false;
    if (localStorage.getItem('sunshines') === null) return false;
    if (localStorage.getItem('sunshinesyield') === null) return false;
    if (localStorage.getItem('indexations') === null) return false;
    if (localStorage.getItem('preconisationsChauffage') === null) return false;
    if (localStorage.getItem('investmentBonus') === null) return false;
    if (localStorage.getItem('customerFlowParameters') === null) return false;
    if (localStorage.getItem('financoScales') === null) return false;
    if (localStorage.getItem('investmentBonus') === null) return false;
    if (localStorage.getItem('kourbe') === null) return false;
    if (localStorage.getItem('etudeDeChargePrices') === null) return false;
    return true;
};

//#endregion
